import { useEffect, useRef, useState } from 'react';
import { useStateInRef } from '../../utils/useStateInRef';
import { useBiAction } from '../BiTracking';
const PULL_RESISTANCE = 0.5;
const OVERPULL_RESISTANCE = 0.35;
const PULL_DISTANCE_PROPERTY = '--page-pull-distance';
const DRAGGING_ATTRIBUTE = 'data-dragging';
const REFRESH_THRESHOLD_PX = 64;
const MAX_PULL_PX = 96;
function isScrollable(element) {
    const { overflowY } = getComputedStyle(element);
    return (overflowY === 'auto' || overflowY === 'scroll') && element.scrollHeight > element.clientHeight;
}
function canStartPull(param) {
    let { target, root, content } = param;
    let element = target instanceof Element ? target : null;
    while(element != null){
        if (element.scrollTop > 0) return false;
        if (element === root) return true;
        if (element !== content && isScrollable(element)) return false;
        element = element.parentElement;
    }
    return false;
}
export function draggedForPullDistance(distancePx) {
    const damped = distancePx <= REFRESH_THRESHOLD_PX ? distancePx : REFRESH_THRESHOLD_PX + (distancePx - REFRESH_THRESHOLD_PX) / OVERPULL_RESISTANCE;
    return damped / PULL_RESISTANCE;
}
export function translateYOf(transform) {
    const values = transform.match(/^matrix(3d)?\(([^)]+)\)$/);
    if (values == null) return 0;
    const parts = values[2].split(',').map(Number);
    const translateY = values[1] == null ? parts[5] : parts[13];
    return Number.isFinite(translateY) ? translateY : 0;
}
export function pullDistanceFor(draggedPx) {
    const damped = draggedPx * PULL_RESISTANCE;
    if (damped <= REFRESH_THRESHOLD_PX) return Math.round(damped);
    const overpull = (damped - REFRESH_THRESHOLD_PX) * OVERPULL_RESISTANCE;
    return Math.round(Math.min(REFRESH_THRESHOLD_PX + overpull, MAX_PULL_PX));
}
export function usePullToRefresh(param) {
    let { rootRef, contentRef, enabled, onRefresh, testId } = param;
    const [isPulling, setIsPulling] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const isMountedRef = useRef(true);
    const isRefreshingRef = useRef(false);
    const getOnRefresh = useStateInRef(onRefresh);
    const logBiAction = useBiAction();
    const getTestId = useStateInRef(testId);
    const isArmed = enabled && onRefresh != null;
    useEffect(()=>()=>{
            isMountedRef.current = false;
        }, []);
    useEffect(()=>{
        const root = rootRef.current;
        if (!isArmed || root == null) return;
        let startY = null;
        let distance = 0;
        const renderDistance = (value)=>{
            root.style.setProperty(PULL_DISTANCE_PROPERTY, String(value));
        };
        // WHY: a React class lands a render too late to suppress the spring-back transition.
        const renderDragging = (isDragging)=>{
            if (isDragging) root.setAttribute(DRAGGING_ATTRIBUTE, 'true');
            else root.removeAttribute(DRAGGING_ATTRIBUTE);
        };
        const endPull = ()=>{
            startY = null;
            distance = 0;
            renderDragging(false);
            renderDistance(0);
            setIsPulling(false);
        };
        const handleTouchStart = (event)=>{
            if (isRefreshingRef.current) return;
            if (event.touches.length !== 1) {
                endPull();
                return;
            }
            if (!canStartPull({
                target: event.target,
                root,
                content: contentRef.current
            })) return;
            // WHY: the body may still be animating back from the previous pull, so the new drag has to
            // continue from where it actually is on screen rather than snapping to the finger.
            const settling = contentRef.current == null ? 0 : translateYOf(getComputedStyle(contentRef.current).transform);
            distance = Math.max(Math.round(settling), 0);
            startY = event.touches[0].clientY - draggedForPullDistance(distance);
        };
        const handleTouchMove = (event)=>{
            const touch = event.touches[0];
            if (startY == null || touch == null) return;
            const dragged = touch.clientY - startY;
            if (dragged <= 0) {
                endPull();
                return;
            }
            distance = pullDistanceFor(dragged);
            event.preventDefault();
            renderDragging(true);
            renderDistance(distance);
            setIsPulling(true);
        };
        const refresh = async ()=>{
            var _getTestId;
            logBiAction({
                entityType: 'page',
                entityName: 'pull_to_refresh',
                actionType: 'click',
                data: {
                    test_id: (_getTestId = getTestId()) !== null && _getTestId !== void 0 ? _getTestId : ''
                }
            });
            isRefreshingRef.current = true;
            setIsRefreshing(true);
            try {
                var _getOnRefresh;
                await ((_getOnRefresh = getOnRefresh()) === null || _getOnRefresh === void 0 ? void 0 : _getOnRefresh());
            } finally{
                isRefreshingRef.current = false;
                renderDistance(0);
                if (isMountedRef.current) {
                    setIsRefreshing(false);
                    setIsPulling(false);
                }
            }
        };
        const handleTouchEnd = ()=>{
            if (startY == null) return;
            const reachedThreshold = distance >= REFRESH_THRESHOLD_PX;
            if (!reachedThreshold) {
                endPull();
                return;
            }
            startY = null;
            distance = 0;
            renderDragging(false);
            renderDistance(REFRESH_THRESHOLD_PX);
            // WHY: a failed refresh is surfaced by the page's own data hook; the gesture owns only the indicator.
            void refresh().catch(()=>undefined);
        };
        root.addEventListener('touchstart', handleTouchStart, {
            passive: true
        });
        root.addEventListener('touchmove', handleTouchMove, {
            passive: false
        });
        root.addEventListener('touchend', handleTouchEnd);
        root.addEventListener('touchcancel', endPull);
        return ()=>{
            root.removeEventListener('touchstart', handleTouchStart);
            root.removeEventListener('touchmove', handleTouchMove);
            root.removeEventListener('touchend', handleTouchEnd);
            root.removeEventListener('touchcancel', endPull);
            renderDragging(false);
            renderDistance(0);
            setIsPulling(false);
        };
    }, [
        isArmed,
        rootRef,
        contentRef,
        logBiAction
    ]);
    return {
        isPulling,
        isRefreshing
    };
}
